import { useForm } from "react-hook-form";
import Button from "../../../student/components/generic/button";
import { LicenseInviteState } from "./types";
import { useMutation } from "@tanstack/react-query";
import { executeQuery, unreachableCase } from "../../../utils";
import { useState } from "react";
import { useLogin } from "../../../utils/auth/login";
import { InviteGoogleAuth } from "./InviteGoogleAuth";

type ResetPasswordState = "idle" | "sending" | "sent" | "inactive";

const ResetPasswordButton: React.FC<{
  email: string;
  setEmailError: (e: string) => void;
}> = ({ email, setEmailError }) => {
  const [resetPasswordState, setResetPasswordState] =
    useState<ResetPasswordState>("idle");

  const { mutate: resetPassword } = useMutation({
    mutationFn: (args: null) =>
      executeQuery({
        method: "post",
        path: "/learner/default/reset_password",
        requestBody: { email },
      }),
  });

  const handleForgotPassword = () => {
    setResetPasswordState("sending");
    resetPassword(null, {
      onSuccess: () => {
        setResetPasswordState("sent");
        setTimeout(() => {
          setResetPasswordState("inactive");
        }, 3000);
      },
      onError: (e: any) => {
        setResetPasswordState("idle");
        setEmailError(
          e.message ||
            "There was an error sending the reset password email, please try again."
        );
      },
    });
  };

  switch (resetPasswordState) {
    case "sent":
      return (
        <span className="text-sm text-dm-success-500">
          Reset password email sent!
        </span>
      );
    case "idle":
    case "sending":
      return (
        <Button
          className="!py-0 text-sm"
          type="link"
          onClick={handleForgotPassword}
          disabled={resetPasswordState === "sending"}
          submit={false}
        >
          Forgot Password?
        </Button>
      );
    case "inactive":
      return null;
    default:
      return unreachableCase(resetPasswordState);
  }
};

// Note: this is a separate component to avoid re-rendering the entire
// InviteLogin component (including GoogleLoginButton) on every keystroke
const LoginForm: React.FC<{
  email: string;
  setInviteState: (state: LicenseInviteState) => void;
}> = ({ email, setInviteState }) => {
  const login = useLogin();
  const [isLoggingIn, setIsLoggingIn] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
    setError,
  } = useForm<{ password: string }>();

  const onSubmit = async (data: { password: string }) => {
    setIsLoggingIn(true);
    try {
      const { success } = await login(email, data.password, "learner");
      if (success) {
        setInviteState({ state: "acceptInvite" });
      } else {
        setIsLoggingIn(false);
        setError("password", {
          type: "manual",
          message: "There was an error logging in, please try again.",
        });
      }
    } catch (e: any) {
      setIsLoggingIn(false);
      setError("password", {
        type: "manual",
        message: "Password is incorrect",
      });
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-6">
      <div className="flex flex-col gap-1">
        <span className="text-sm font-bold">Email</span>
        <span>{email}</span>
      </div>
      <div className="flex flex-col items-start gap-1">
        <label className="text-sm font-bold" htmlFor="password">
          Password
        </label>
        <input
          className="h-10 w-full rounded px-3 py-0"
          type="password"
          id="password"
          {...register("password", { required: "This field is required" })}
        />
        {errors.password && (
          <span className="text-sm text-dm-error-500">
            {errors.password.message}
          </span>
        )}
        <ResetPasswordButton
          email={email}
          setEmailError={(message: string) =>
            setError("password", { type: "manual", message })
          }
        />
      </div>
      <Button isLoading={isLoggingIn}>Continue</Button>
    </form>
  );
};

export const InviteLogin: React.FC<{
  email: string;
  accountType: "parent" | "learner";
  setInviteState: (state: LicenseInviteState) => void;
}> = ({ email, accountType, setInviteState }) => {
  return (
    <>
      <div className="flex flex-col gap-2 text-center">
        <h1 className="font-serif text-2xl font-bold text-dm-brand-blue-600">
          Log In
        </h1>
        <p className="font-serif text-lg font-bold">
          You already have an account. Log in below using your password.
        </p>
      </div>

      <LoginForm email={email} setInviteState={setInviteState} />

      <span className="w-full text-center font-bold">Or</span>
      <InviteGoogleAuth
        accountType={accountType}
        setInviteState={setInviteState}
      />
    </>
  );
};
